import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import AlertModal from './AlertModal';
import { extractEquationOutputs } from '../equationOutputs';

const AddDimensionModal = ({ quotaProjectId, dimension, cells = [], overallTarget, existingDimensions = [], pendingModifications = [], onClose, onSuccess }) => {
  // Editable form: only Escape and the explicit ✕ / Annuler buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [label, setLabel] = useState(dimension?.label || '');
  const [questionId, setQuestionId] = useState(dimension?.limesurvey_question_id || '');
  const [questionCode, setQuestionCode] = useState(dimension?.limesurvey_question_code || '');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [subquestionId, setSubquestionId] = useState(dimension?.subquestion_id || '');
  const [subquestionCode, setSubquestionCode] = useState(dimension?.subquestion_code || '');
  const [selectedSubquestion, setSelectedSubquestion] = useState(null);
  const [itemCode, setItemCode] = useState(dimension?.item_code || '');
  const [values, setValues] = useState(
    dimension?.values_json?.map(v => ({
      ...v,
      target_quota: v.target_quota ?? 0
    })) || [{ code: '', label: '', order_index: 1, target_quota: 0 }]
  );
  const [loading, setLoading] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [groups, setGroups] = useState([]);
  const [showQuestionPicker, setShowQuestionPicker] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // Codes already used by cells of this dimension: renaming one would orphan those cells.
  const codesUsedByCells = new Set(
    dimension
      ? cells.map(c => (c.dimension_values_json || {})[dimension.id]).filter(Boolean)
      : []
  );

  useEffect(() => {
    if (!dimension) {
      fetchQuestions();
    }
  }, []);

  const fetchQuestions = async (groupId = null) => {
    try {
      setLoadingQuestions(true);
      setQuestionsError(false);
      const params = {};
      if (groupId) params.group_id = groupId;

      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/dimensions/limesurvey-questions`,
        { params }
      );
      // Keep the full group list when filtering: it only needs loading once.
      if (!groupId || groups.length === 0) {
        setGroups(response.data.groups || []);
      }
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
      setQuestionsError(true);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleAddValue = () => {
    setValues([...values, { code: '', label: '', order_index: values.length + 1, target_quota: 0 }]);
  };

  const handleRemoveValue = (index) => {
    setValues(values.filter((_, i) => i !== index));
  };

  const handleValueChange = (index, field, value) => {
    const newValues = [...values];
    newValues[index] = { ...newValues[index], [field]: value };
    setValues(newValues);
  };

  const handleQuestionSelect = async (question) => {
    const qid = question.qid || question.id;

    // The question list comes without properties (too slow to load for every
    // question); fetch them for the picked one before auto-populating values.
    if (!question.properties) {
      try {
        setLoadingProperties(true);
        setShowQuestionPicker(false);
        const response = await axios.get(
          `/quotas/projects/${quotaProjectId}/dimensions/limesurvey-questions/${qid}`
        );
        question = { ...question, properties: response.data.properties };
        setQuestions(prev => prev.map(q => ((q.qid || q.id) === qid ? question : q)));
      } catch (error) {
        console.error('Error fetching question properties:', error);
        setAlertModal({
          show: true,
          title: 'Erreur',
          message: 'Impossible de charger les détails de cette question depuis LimeSurvey. Veuillez réessayer.',
          type: 'error'
        });
        return;
      } finally {
        setLoadingProperties(false);
      }
    }

    setQuestionId(qid);
    // Use question code (title) instead of full question text
    setQuestionCode(question.title || question.code || question.qid || question.id || '');
    setSelectedQuestion(question);
    setShowQuestionPicker(false);
    
    // Reset subquestion and item when question changes
    setSubquestionId('');
    setSubquestionCode('');
    setSelectedSubquestion(null);
    setItemCode('');

    // Check if question is array type (has subquestions)
    const questionType = question.properties?.type || question.type;
    const hasSubquestions = question.properties?.subquestions && 
                           Object.keys(question.properties.subquestions).length > 0;
    
    // Get answeroptions (can be in properties.answeroptions or properties.answerOptions)
    const answerOptions = question.properties?.answeroptions || 
                         question.properties?.answerOptions || 
                         question.answeroptions ||
                         question.answerOptions;

    // Get subquestions
    const subquestions = question.properties?.subquestions || question.subquestions || {};

    // Check if this is type 'M' (Multiple choice) - for this type, subquestions are the values
    const isMultipleChoice = questionType === 'M';
    
    // Check if this is type 'G' (Gender) - predefined question type with fixed values
    const isGender = questionType === 'G';
    
    // Check if this is type 'Y' (Yes/No) - predefined question type with fixed values
    const isYesNo = questionType === 'Y';

    // Check if this is type '*' (Equation) - values come from its expression
    const isEquation = questionType === '*';

    // Determine if this is truly an array question that requires subquestion selection
    // Type 'L' (List) and other simple types have answerOptions directly, not subquestions
    // Only array types like 'F' (Array), '1' (Array (Yes/No/Uncertain)), etc. need subquestions
    // BUT: Type 'M' (Multiple choice) uses subquestions as values, not answerOptions
    const isTrulyArrayQuestion = questionType && typeof questionType === 'string' && 
                                 (questionType.includes('array') || 
                                  questionType === 'F' || 
                                  questionType === '1' ||
                                  questionType === 'H' || 
                                  questionType === 'E') &&
                                 hasSubquestions &&
                                 (!answerOptions || Object.keys(answerOptions).length === 0);

    // Debug: log what we have
    console.log('Selected question:', {
      qid,
      type: questionType,
      isMultipleChoice,
      isGender,
      isYesNo,
      isTrulyArrayQuestion,
      hasSubquestions,
      subquestionsCount: subquestions ? Object.keys(subquestions).length : 0,
      hasAnswerOptions: !!answerOptions,
      answerOptionsCount: answerOptions ? Object.keys(answerOptions).length : 0,
      answerOptionsKeys: answerOptions ? Object.keys(answerOptions) : [],
      answerOptions: answerOptions,
      subquestions: subquestions
    });

    // Auto-populate values based on question type
    // PRIORITY 1: Type 'G' (Gender) - predefined question type with fixed values
    if (isGender) {
      console.log('✅ Type G (Gender) detected - using predefined gender values');
      
      // Type G is a built-in LimeSurvey question with fixed options:
      // M = Male (Homme)
      // F = Female (Femme)
      // The API doesn't return answerOptions for this type, so we create them manually
      const genderValues = [
        {
          code: 'M',
          label: 'Homme',
          limesurvey_answer_code: 'M',
          order_index: 1,
          is_active: true,
          target_quota: 0
        },
        {
          code: 'F',
          label: 'Femme',
          limesurvey_answer_code: 'F',
          order_index: 2,
          is_active: true,
          target_quota: 0
        }
      ];
      
      console.log('✅ Auto-populated gender values:', genderValues);
      setValues(genderValues);
    }
    // PRIORITY 1b: Type 'Y' (Yes/No) - predefined question type with fixed values
    else if (isYesNo) {
      console.log('✅ Type Y (Yes/No) detected - using predefined Yes/No values');
      
      // Type Y is a built-in LimeSurvey question with fixed options:
      // Y = Yes (Oui)
      // N = No (Non)
      // The API doesn't return answerOptions for this type, so we create them manually
      const yesNoValues = [
        {
          code: 'Y',
          label: 'Oui',
          limesurvey_answer_code: 'Y',
          order_index: 1,
          is_active: true,
          target_quota: 0
        },
        {
          code: 'N',
          label: 'Non',
          limesurvey_answer_code: 'N',
          order_index: 2,
          is_active: true,
          target_quota: 0
        }
      ];
      
      console.log('✅ Auto-populated Yes/No values:', yesNoValues);
      setValues(yesNoValues);
    }
    
    // PRIORITY 2: Type 'M' (Multiple choice) - use subquestions as values
    if (isMultipleChoice && hasSubquestions && Object.keys(subquestions).length > 0) {
      console.log('✅ Type M (Multiple choice) detected - using subquestions as values');
      console.log('📋 Subquestions structure:', subquestions);
      let newValues = [];
      
      // Object format: {SQ001: {title: "...", code: "SQ001", ...}, SQ002: {...}, ...}
      // Sort by key (SQ001, SQ002, etc.) or by code if available
      const entries = Object.entries(subquestions);
      entries.sort((a, b) => {
        // Try to sort by code in data, otherwise by key
        const codeA = a[1]?.code || a[0];
        const codeB = b[1]?.code || b[0];
        return String(codeA).localeCompare(String(codeB));
      });
      
      newValues = entries.map(([key, data], idx) => {
        // Debug: log the structure
        console.log(`Subquestion [${idx}]:`, { key, data, dataType: typeof data, dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'N/A' });
        
        // For subquestions in type M:
        // - Code input should show: data.title (the subquestion code like "SQ002")
        // - Label input should show: data.question (the content/text like "bruns")
        
        // If data is a string, it's the content directly
        if (typeof data === 'string') {
          return {
            code: String(key), // Use key as code (fallback)
            label: String(data).trim(), // Content is the string itself
            limesurvey_answer_code: String(key),
            order_index: idx + 1,
            is_active: true,
            target_quota: 0
          };
        }
        
        // If data is an object
        if (data && typeof data === 'object') {
          // Code = data.title (the subquestion code, e.g., "SQ002")
          const subquestionCode = data.title || data.code || String(key);
          
          // Label = data.question (the content/text of the subquestion, e.g., "bruns")
          const subquestionContent = data.question || data.text || String(data);
          
          console.log(`  → Extracted: code="${subquestionCode}", label="${subquestionContent}"`);
          
          return {
            code: String(subquestionCode),
            label: String(subquestionContent).trim(),
            limesurvey_answer_code: String(subquestionCode),
            order_index: idx + 1,
            is_active: true,
            target_quota: 0
          };
        }
        
        // Fallback
        return {
          code: String(key),
          label: String(data || key).trim(),
          limesurvey_answer_code: String(key),
          order_index: idx + 1,
          is_active: true,
          target_quota: 0
        };
      });
      
      if (newValues.length > 0) {
        console.log('✅ Auto-populated values from subquestions (type M):', newValues);
        setValues(newValues);
      } else {
        console.warn('⚠️ No subquestions found for type M question');
        setValues([{ code: '', label: '', order_index: 1 }]);
      }
    }
    // PRIORITY 2b: Type '*' (Equation) - no answer options in LimeSurvey; the values
    // are the literal results of its expression, which is the question text itself.
    else if (isEquation) {
      const { values: outputs, complete, canBeEmpty } = extractEquationOutputs(
        question.question || question.properties?.question || ''
      );

      setValues(outputs.length > 0
        ? outputs.map((code, idx) => ({
            code,
            label: code,
            order_index: idx + 1,
            is_active: true,
            target_quota: 0
          }))
        : [{ code: '', label: '', order_index: 1, is_active: true, target_quota: 0 }]);

      const notes = [];
      if (!complete) {
        notes.push("Certaines valeurs de l'équation n'ont pas pu être lues automatiquement : ajoutez les codes manquants avec « Ajouter une valeur ».");
      }
      if (canBeEmpty) {
        notes.push("L'équation peut renvoyer une valeur vide : ces réponses ne seront comptées dans aucune cellule.");
      }
      if (notes.length > 0) {
        setAlertModal({ show: true, title: 'Question équation', message: notes.join('\n\n'), type: 'warning' });
      }
    }
    // PRIORITY 3: Use answerOptions directly if available (for types like 'L', etc.)
    // This takes precedence over subquestions for non-M, non-G, and non-Y types, even if the question has both
    else if (!isMultipleChoice && !isGender && !isYesNo) {
      const hasValidAnswerOptions = answerOptions && 
                                    typeof answerOptions === 'object' && 
                                    answerOptions !== null &&
                                    !Array.isArray(answerOptions) &&
                                    Object.keys(answerOptions).length > 0;
      
      console.log('🔍 Decision check:', {
        hasValidAnswerOptions,
        answerOptionsType: typeof answerOptions,
        answerOptionsIsArray: Array.isArray(answerOptions),
        answerOptionsKeys: answerOptions ? Object.keys(answerOptions) : null,
        isTrulyArrayQuestion,
        willUseAnswerOptions: hasValidAnswerOptions,
        willWaitForSubquestion: !hasValidAnswerOptions && isTrulyArrayQuestion
      });

      if (hasValidAnswerOptions) {
        // Question has direct answer options - use them immediately
        console.log('✅ Using direct answerOptions, ignoring subquestions');
        let newValues = [];
        
        // Object format: {AO01: {answer: "...", ...}, AO02: {...}, ...}
        // Sort by order if available, otherwise by key
        const entries = Object.entries(answerOptions);
        entries.sort((a, b) => {
          const orderA = a[1]?.order !== undefined ? parseInt(a[1].order) : 999;
          const orderB = b[1]?.order !== undefined ? parseInt(b[1].order) : 999;
          return orderA - orderB;
        });
        
        newValues = entries.map(([code, data], idx) => {
          // data is an object with answer property
          const label = typeof data === 'object' && data !== null
            ? (data.answer || data.title || data.label || data.text || code)
            : String(data || code);
          
          return {
            code: String(code),
            label: String(label).trim(),
            limesurvey_answer_code: String(code),
            order_index: (data?.order !== undefined ? parseInt(data.order) + 1 : idx + 1),
            is_active: true,
            target_quota: 0
          };
        });
        
        if (newValues.length > 0) {
          console.log('✅ Auto-populated values:', newValues);
          setValues(newValues);
        } else {
          console.warn('⚠️ No values could be extracted from answeroptions', {
            answerOptions,
            type: typeof answerOptions,
            isArray: Array.isArray(answerOptions)
          });
          setValues([{ code: '', label: '', order_index: 1 }]);
        }
      } else if (isTrulyArrayQuestion && hasSubquestions) {
        // For true array questions with subquestions but no direct answerOptions
        // Show subquestion selector - Values will be populated when subquestion is selected
        console.log('Array question detected (no direct answerOptions), waiting for subquestion selection');
        setValues([]);
      } else {
        // No answer options found
        console.warn('⚠️ No answeroptions found in question properties', {
          questionType,
          hasProperties: !!question.properties,
          propertiesKeys: question.properties ? Object.keys(question.properties) : [],
          hasSubquestions
        });
        setValues([{ code: '', label: '', order_index: 1 }]);
      }
    }
  };

  const handleSubquestionSelect = (subquestion) => {
    // If subquestion is null/empty, reset to question principale
    if (!subquestion || !subquestion.code) {
      setSubquestionId('');
      setSubquestionCode('');
      setSelectedSubquestion(null);
      setItemCode('');
      
      // Restore answerOptions from main question
      const mainAnswerOptions = selectedQuestion?.properties?.answeroptions || 
                               selectedQuestion?.properties?.answerOptions || 
                               selectedQuestion?.answeroptions ||
                               selectedQuestion?.answerOptions;
      
      if (mainAnswerOptions && typeof mainAnswerOptions === 'object' && 
          !Array.isArray(mainAnswerOptions) && Object.keys(mainAnswerOptions).length > 0) {
        const entries = Object.entries(mainAnswerOptions);
        entries.sort((a, b) => {
          const orderA = a[1]?.order !== undefined ? parseInt(a[1].order) : 999;
          const orderB = b[1]?.order !== undefined ? parseInt(b[1].order) : 999;
          return orderA - orderB;
        });
        
        const newValues = entries.map(([code, data], idx) => ({
          code: String(code),
          label: String(data.answer || data.title || data.label || data.text || code).trim(),
          limesurvey_answer_code: String(code),
          order_index: (data?.order !== undefined ? parseInt(data.order) + 1 : idx + 1),
          is_active: true,
          target_quota: 0
        }));
        setValues(newValues);
      } else {
        setValues([{ code: '', label: '', order_index: 1 }]);
      }
      return;
    }
    
    // For subquestions, we need to store:
    // - subquestion_id: the numeric ID or the key from subquestions object (if available)
    // - subquestion_code: the SQ code (SQ001, SQ002, etc.) which is what LimeSurvey uses in responses
    // subquestion.code is the key from the subquestions object (could be SQ001, SQ002, or numeric ID)
    // subquestion.title is usually the SQ code (SQ001, SQ002, etc.)
    const subqKey = subquestion.code || ''; // The key in the subquestions object (for reference/selection)
    const sqCode = subquestion.title || (subquestion.code && String(subquestion.code).match(/^SQ\d+$/) ? subquestion.code : null);
    const subqId = subquestion.id || subquestion.code || '';
    
    setSubquestionId(subqId);
    // Store the SQ code (SQ001, SQ002, etc.) for subquestion_code - this is what LimeSurvey uses in responses
    // If we don't have title, but code looks like SQ code, use it
    // Otherwise, we'll need to handle numeric IDs in the backend
    setSubquestionCode(sqCode || subqKey || '');
    setSelectedSubquestion(subquestion);
    
    // Reset item when subquestion changes
    setItemCode('');

    console.log('Selected subquestion:', subquestion);
    
    // For array questions (type F), answerOptions are usually in the main question properties
    // Try to get answerOptions from subquestion first, then from main question
    let answerOptions = subquestion.answeroptions || subquestion.answerOptions;
    
    // If not in subquestion, try main question (common for type F)
    if (!answerOptions || (typeof answerOptions === 'object' && Object.keys(answerOptions).length === 0)) {
      answerOptions = selectedQuestion?.properties?.answeroptions || 
                     selectedQuestion?.properties?.answerOptions || 
                     selectedQuestion?.answeroptions ||
                     selectedQuestion?.answerOptions;
    }
    
    console.log('AnswerOptions for subquestion:', answerOptions);

    // Auto-populate values from answer options
    if (answerOptions && typeof answerOptions === 'object' && 
        !Array.isArray(answerOptions) && Object.keys(answerOptions).length > 0) {
      const entries = Object.entries(answerOptions);
      entries.sort((a, b) => {
        const orderA = a[1]?.order !== undefined ? parseInt(a[1].order) : 999;
        const orderB = b[1]?.order !== undefined ? parseInt(b[1].order) : 999;
        return orderA - orderB;
      });
      
      const newValues = entries.map(([code, data], idx) => ({
        code: String(code),
        label: String(data.answer || data.title || data.label || data.text || code).trim(),
        limesurvey_answer_code: String(code),
        order_index: (data?.order !== undefined ? parseInt(data.order) + 1 : idx + 1),
        is_active: true,
        target_quota: 0
      }));
      
      console.log('✅ Auto-populated values from subquestion answerOptions:', newValues);
      setValues(newValues);
    } else {
      console.warn('⚠️ No answerOptions found for subquestion');
      setValues([{ code: '', label: '', order_index: 1 }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!label || !questionId || !questionCode) {
      setAlertModal({
        show: true,
        title: 'Champs requis',
        message: 'Veuillez remplir tous les champs obligatoires',
        type: 'warning'
      });
      return;
    }

    if (values.length === 0 || values.some(v => !v.code || !v.label)) {
      setAlertModal({
        show: true,
        title: 'Valeurs requises',
        message: 'Veuillez définir au moins une valeur avec code et label',
        type: 'warning'
      });
      return;
    }

    // If editing a dimension, validate that target_quota is not reduced below allocated quota
    if (dimension && dimension.id) {
      const quotaViolations = [];
      
      values.forEach(value => {
        const newTargetQuota = parseInt(value.target_quota) || 0;
        
        // Calculate allocated quota for this value in existing cells
        // Uses pending target values when available (so pending decreases free up quota)
        const allocatedQuota = cells
          .filter(c => {
            const cellValues = c.dimension_values_json || {};
            return cellValues[dimension.id] === value.code;
          })
          .reduce((sum, c) => {
            // Pending deletion = count as 0
            const hasPendingDelete = pendingModifications.some(
              pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'delete'
            );
            if (hasPendingDelete) return sum;
            // Pending target change = use new value
            const pendingMod = pendingModifications.find(
              pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'target_count'
            );
            const effectiveTarget = pendingMod ? (parseInt(pendingMod.new_value) || 0) : (c.target_count || 0);
            return sum + effectiveTarget;
          }, 0);
        
        // Check if new target_quota is less than allocated quota
        if (newTargetQuota < allocatedQuota) {
          quotaViolations.push({
            value: value.label || value.code,
            allocated: allocatedQuota,
            requested: newTargetQuota,
            minimum: allocatedQuota
          });
        }
      });
      
      if (quotaViolations.length > 0) {
        const messages = quotaViolations.map(v => 
          `${v.value}: quota alloué ${v.allocated}, quota demandé ${v.requested}. Minimum requis: ${v.minimum}`
        );
        setAlertModal({
          show: true,
          title: 'Quota insuffisant',
          message: `Vous ne pouvez pas réduire le quota en dessous du quota déjà alloué dans les cellules existantes:\n${messages.join('\n')}\n\nVeuillez d'abord réduire ou supprimer les cellules qui utilisent ces valeurs.`,
          type: 'error'
        });
        return;
      }
    }

    // Check for duplicate dimensions (same question + same subquestion)
    // Exclude current dimension when editing
    const duplicateDimension = existingDimensions.find(existingDim => {
      // Skip if this is the dimension being edited
      if (dimension && dimension.id && existingDim.id === dimension.id) {
        return false;
      }
      
      // Check if question matches
      const questionMatches = 
        (existingDim.limesurvey_question_id && existingDim.limesurvey_question_id === questionId) ||
        (existingDim.limesurvey_question_code && existingDim.limesurvey_question_code === questionCode);
      
      if (!questionMatches) {
        return false;
      }
      
      // If question matches, check subquestion
      // Both null/empty = match
      // Both have same value = match
      // One has subquestion and other doesn't = NOT a match (different dimensions)
      const existingSubquestionId = existingDim.subquestion_id || null;
      const existingSubquestionCode = existingDim.subquestion_code || null;
      const newSubquestionId = subquestionId || null;
      const newSubquestionCode = subquestionCode || null;
      
      // Normalize: treat empty string as null
      const existingHasSubquestion = existingSubquestionId || existingSubquestionCode;
      const newHasSubquestion = newSubquestionId || newSubquestionCode;
      
      // If one has subquestion and other doesn't, they're different
      if (existingHasSubquestion && !newHasSubquestion) {
        return false;
      }
      if (!existingHasSubquestion && newHasSubquestion) {
        return false;
      }
      
      // Both have subquestions or both don't - check if they match
      if (existingHasSubquestion && newHasSubquestion) {
        // Match if subquestion_id OR subquestion_code matches
        const subquestionMatches = 
          (existingSubquestionId && existingSubquestionId === newSubquestionId) ||
          (existingSubquestionCode && existingSubquestionCode === newSubquestionCode);
        
        return subquestionMatches;
      }
      
      // Both don't have subquestions - it's a duplicate
      return true;
    });
    
    if (duplicateDimension) {
      const duplicateInfo = duplicateDimension.subquestion_code 
        ? `Question: ${duplicateDimension.limesurvey_question_code}, Sous-question: ${duplicateDimension.subquestion_code}`
        : `Question: ${duplicateDimension.limesurvey_question_code}`;
      
      setAlertModal({
        show: true,
        title: 'Dimension dupliquée',
        message: `Une dimension avec la même question et sous-question existe déjà:\n${duplicateInfo}\n\nVous pouvez créer une dimension avec la même question mais une sous-question différente.`,
        type: 'error'
      });
      return;
    }

    // Hand-entered codes (equations, questions without answer options) must be filled and unique
    const codes = values.map(v => String(v.code ?? '').trim());
    if (codes.some(c => c === '')) {
      setAlertModal({
        show: true,
        title: 'Code manquant',
        message: 'Chaque valeur doit avoir un code.',
        type: 'warning'
      });
      return;
    }
    const duplicateCodes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (duplicateCodes.length > 0) {
      setAlertModal({
        show: true,
        title: 'Code dupliqué',
        message: `Ces codes apparaissent plusieurs fois : ${[...new Set(duplicateCodes)].join(', ')}`,
        type: 'warning'
      });
      return;
    }

    // Validate quota distribution
    if (overallTarget !== null && overallTarget !== undefined) {
      const totalQuota = values.reduce((sum, v) => sum + (parseInt(v.target_quota) || 0), 0);
      
      if (totalQuota !== overallTarget) {
        setAlertModal({
          show: true,
          title: 'Quota invalide',
          message: `Le total des quotas des valeurs (${totalQuota.toLocaleString()}) doit être égal au quota générale (${overallTarget.toLocaleString()})`,
          type: 'error'
        });
        return;
      }

      // Check if at least one value has quota > 0
      const hasQuota = values.some(v => (parseInt(v.target_quota) || 0) > 0);
      if (!hasQuota) {
        setAlertModal({
          show: true,
          title: 'Quota requis',
          message: 'Au moins une valeur doit avoir un quota supérieur à 0',
          type: 'warning'
        });
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        label,
        limesurvey_question_id: questionId,
        limesurvey_question_code: questionCode,
        subquestion_id: subquestionId || null,
        subquestion_code: subquestionCode || null,
        item_code: itemCode || null,
        values_json: values.map((v, idx) => ({
          ...v,
          order_index: v.order_index || idx + 1,
          is_active: v.is_active !== false
        }))
      };

      let response;
      if (dimension) {
        response = await axios.put(
          `/quotas/projects/${quotaProjectId}/dimensions/${dimension.id}`,
          payload
        );
      } else {
        response = await axios.post(
          `/quotas/projects/${quotaProjectId}/dimensions`,
          payload
        );
      }

      onSuccess(response.data.data);
    } catch (error) {
      console.error('Error saving dimension:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la sauvegarde',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" {...dismiss}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{dimension ? 'Modifier la dimension' : 'Ajouter une dimension'}</h2>
          <button className="btn-icon" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Label de la dimension *</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ex: Région, Genre, Âge"
              required
            />
          </div>

          <div className="form-group">
            <label>Groupe LimeSurvey</label>
            <select
              value={selectedGroupId || ''}
              disabled={loadingQuestions && groups.length === 0}
              onChange={(e) => {
                setSelectedGroupId(e.target.value);
                fetchQuestions(e.target.value || null);
              }}
            >
              <option value="">
                {loadingQuestions && groups.length === 0 ? 'Chargement des groupes...' : 'Tous les groupes'}
              </option>
              {groups.map(group => (
                <option key={group.gid || group.id} value={group.gid || group.id}>
                  {group.group_name || group.title || group.id}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Question LimeSurvey *</label>
            <div className="question-picker">
              <input
                type="text"
                value={loadingProperties ? 'Chargement de la question...' : (questionCode || questionId)}
                readOnly
                disabled={loadingProperties}
                placeholder={loadingQuestions ? 'Chargement des questions...' : 'Sélectionner une question'}
                onClick={() => setShowQuestionPicker(!showQuestionPicker)}
              />
              {showQuestionPicker && (
                <div className="question-dropdown">
                  {loadingQuestions ? (
                    <div>Chargement...</div>
                  ) : questionsError ? (
                    <div>
                      Erreur de chargement depuis LimeSurvey.{' '}
                      <button type="button" onClick={() => fetchQuestions(selectedGroupId || null)}>
                        Réessayer
                      </button>
                    </div>
                  ) : questions.length === 0 ? (
                    <div>Aucune question trouvée</div>
                  ) : (
                    questions.map(q => (
                      <div
                        key={q.qid || q.id}
                        className="question-item"
                        onClick={() => handleQuestionSelect(q)}
                      >
                        <div className="question-item-title">
                          {q.title || q.code || q.qid || q.id}
                        </div>
                        {(q.properties?.type || q.type) && (
                          <div className="question-item-type">
                            Type: {q.properties?.type || q.type}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {selectedQuestion &&
            selectedQuestion.properties?.subquestions &&
            (selectedQuestion.properties?.type === 'F' || selectedQuestion.type === 'F') && (
            <div className="form-group">
              <label>Sous-question</label>
              <select
                value={selectedSubquestion?.code || subquestionCode || ''}
                onChange={(e) => {
                  if (!e.target.value) {
                    // Reset to question principale
                    handleSubquestionSelect(null);
                  } else {
                    // Find the subquestion by its key (code) in the subquestions object
                    const subq = Object.entries(selectedQuestion.properties.subquestions)
                      .find(([code]) => code === e.target.value);
                    if (subq) {
                      // subq[0] is the key (code), subq[1] is the data object
                      handleSubquestionSelect({ 
                        code: subq[0], // The key (used for selection)
                        id: subq[1]?.id || subq[0], // ID if available, otherwise use key
                        title: subq[1]?.title || subq[1]?.code || subq[0], // SQ code (SQ001, etc.)
                        ...subq[1] // All other properties
                      });
                    }
                  }
                }}
              >
                <option value="">-- Aucune (question principale) --</option>
                {Object.entries(selectedQuestion.properties.subquestions).map(([code, data]) => {
                  // Display: question text or code, with SQ code if available
                  const displayText = data.question || data.title || code;
                  const sqCode = data.title || (code.match(/^SQ\d+$/) ? code : null);
                  return (
                    <option key={code} value={code}>
                      {sqCode ? `${sqCode}: ${displayText}` : displayText}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {selectedSubquestion && selectedSubquestion.items && (
            <div className="form-group">
              <label>Item spécifique (optionnel)</label>
              <select
                value={itemCode || ''}
                onChange={(e) => setItemCode(e.target.value)}
              >
                <option value="">-- Tous les items --</option>
                {selectedSubquestion.items.map(item => (
                  <option key={item.code || item.id} value={item.code || item.id}>
                    {item.label || item.code || item.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Valeurs *</label>
            {overallTarget !== null && overallTarget !== undefined && (
              <div className="quota-info" style={{ marginBottom: '10px', padding: '8px', background: '#f0f0f0', borderRadius: '4px' }}>
                <strong>Quota générale: {overallTarget.toLocaleString()}</strong>
                <span style={{ marginLeft: '15px' }}>
                  Total alloué: <strong>{values.reduce((sum, v) => sum + (parseInt(v.target_quota) || 0), 0).toLocaleString()}</strong>
                  {values.reduce((sum, v) => sum + (parseInt(v.target_quota) || 0), 0) !== overallTarget && (
                    <span style={{ color: '#dc3545', marginLeft: '10px' }}>
                      (Manque: {(overallTarget - values.reduce((sum, v) => sum + (parseInt(v.target_quota) || 0), 0)).toLocaleString()})
                    </span>
                  )}
                </span>
              </div>
            )}
            <div className="values-list">
              {values.map((value, index) => (
                <div key={index} className="value-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  {value.limesurvey_answer_code || codesUsedByCells.has(value.code) ? (
                    <input
                      type="text"
                      placeholder="Code"
                      value={value.code}
                      readOnly
                      title="Le code est lié à LimeSurvey et ne peut pas être modifié"
                      style={{ flex: '1', background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder="Code (valeur exacte dans LimeSurvey)"
                      value={value.code}
                      onChange={(e) => handleValueChange(index, 'code', e.target.value.trim())}
                      required
                      style={{ flex: '1' }}
                    />
                  )}
                  <input
                    type="text"
                    placeholder="Label"
                    value={value.label}
                    onChange={(e) => handleValueChange(index, 'label', e.target.value)}
                    required
                    style={{ flex: '2' }}
                  />
                  {overallTarget !== null && overallTarget !== undefined && (
                    <input
                      type="number"
                      placeholder="Quota"
                      value={value.target_quota || 0}
                      onChange={(e) => {
                        const quota = Math.max(0, parseInt(e.target.value) || 0);
                        handleValueChange(index, 'target_quota', quota);
                      }}
                      min="0"
                      max={overallTarget}
                      style={{ width: '120px' }}
                    />
                  )}
                  <button
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => handleRemoveValue(index)}
                    title="Supprimer"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
              {values.some(v => !v.limesurvey_answer_code) && (
                <button type="button" className="btn-secondary" onClick={handleAddValue}>
                  <FontAwesomeIcon icon={faPlus} /> Ajouter une valeur
                </button>
              )}
              {values.length === 0 && (
                <div className="info-message">
                  <p>Les valeurs seront automatiquement récupérées depuis LimeSurvey lorsque vous sélectionnez une question.</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Sauvegarde...' : dimension ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default AddDimensionModal;

